import { useState } from 'react';
import type { AdminOrganizationDetail } from '@traceio-org/platform-contracts';
import { PageSection } from '@traceio-org/ui';
import { RadioGroup, ReasonField, reasonError } from '../../../components/fields';
import { WriteDialog } from '../../../components/WriteDialog';
import { useAdmin } from '../../../context';

const CHOICES = [
  {
    value: 'billed',
    label: 'Billed',
    description: 'Its modules come from its contracts, and its usage is billed.',
  },
  {
    value: 'exempt',
    label: 'Internal: not billed',
    description:
      'Every module of its products with no contract and no card. Usage is recorded but never billed, and it is never linked to Stripe.',
  },
] as const;
type Choice = (typeof CHOICES)[number]['value'];

/**
 * Internal organizations (spec 2026-10-18 piece 1): whether the organization is billed, and why not when it is
 * internal. Staff with staff.billing.manage change it; platform-api refuses while a contract is in force or scheduled.
 */
export function ExemptionSection({
  organization,
  canManage,
  onChange,
}: {
  organization: AdminOrganizationDetail;
  canManage: boolean;
  onChange: () => void;
}) {
  return (
    <PageSection
      title="Internal organization"
      description="Trace's own organization, and demo or partner ones, use their products without a contract and are never billed."
      action={
        canManage ? (
          <button type="button" className="admin-action" onClick={onChange}>
            Change billing
          </button>
        ) : undefined
      }
    >
      {organization.billingExempt ? (
        <>
          <p>This organization is internal: it is not billed.</p>
          <p className="admin-muted">Reason: {organization.billingExemptReason}</p>
        </>
      ) : (
        <p>Billed through its contracts.</p>
      )}
    </PageSection>
  );
}

/** The switch between billed and internal, with the reason recorded in the audit log. */
export function ExemptionDialog({
  organization,
  open,
  onClose,
  onSaved,
}: {
  organization: AdminOrganizationDetail;
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { api } = useAdmin();
  const [choice, setChoice] = useState<Choice>(organization.billingExempt ? 'exempt' : 'billed');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // platform-api answers an unchanged request without changing anything, so Save is offered only for a change: the
  // other choice, or (staying internal) a different reason, compared trimmed as platform-api stores it.
  const unchanged =
    choice === 'exempt'
      ? organization.billingExempt && reason.trim() === organization.billingExemptReason
      : !organization.billingExempt;
  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title={`Billing of ${organization.displayName}`}
      description="An organization with a contract in force or scheduled cannot be made internal, and no contract can be signed for an internal one."
      submitLabel="Save"
      submitDisabled={unchanged}
      onSubmit={async () => {
        const found = reasonError(reason);
        setError(found);
        if (found) return 'invalid';
        await api.setBillingExemption(organization.id, choice === 'exempt', reason);
        return undefined;
      }}
      onSaved={() =>
        onSaved(
          choice === 'exempt'
            ? `${organization.displayName} is internal: it is not billed.`
            : `${organization.displayName} is billed again.`,
        )
      }
    >
      <RadioGroup
        legend="Billing"
        value={choice}
        onChange={(value) => setChoice(value as Choice)}
        choices={CHOICES}
      />
      <ReasonField value={reason} onChange={setReason} error={error} />
    </WriteDialog>
  );
}
